import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useCart } from '../contexts/CartContext';
import { api } from '../utils/api';
import {
  clearPendingCheckouts,
  getCheckoutProgress,
  loadPendingCheckouts,
  savePendingCheckouts,
} from '../utils/checkoutPending';

function confirmedKey(sessionId) {
  return `checkoutConfirmed:${sessionId}`;
}

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { clearCart, removeItemsByIds } = useCart();
  const [handled, setHandled] = useState(false);
  const [fulfillError, setFulfillError] = useState(null);
  const [fulfilling, setFulfilling] = useState(true);
  const [progress, setProgress] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (handled || ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;

    (async () => {
      const sessionId = searchParams.get('session_id');
      const { checkouts, currentIndex } = loadPendingCheckouts();
      const paidIndex = Number.isFinite(currentIndex) ? currentIndex : 0;

      if (sessionId) {
        const key = confirmedKey(sessionId);
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, 'pending');
          try {
            const res = await api('/api/payments/confirm-session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId }),
              getToken,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok && !cancelled) {
              sessionStorage.removeItem(key);
              setFulfillError(data.error || `Could not finalize order (${res.status})`);
            } else {
              sessionStorage.setItem(key, 'done');
            }
          } catch (err) {
            sessionStorage.removeItem(key);
            if (!cancelled) setFulfillError(err.message || 'Could not finalize order');
          }
        }
      }

      if (cancelled) return;

      // Trim cart for the vendor just paid
      if (checkouts.length > 0 && paidIndex >= 0 && paidIndex < checkouts.length) {
        const paid = checkouts[paidIndex];
        if (paid?.itemIds?.length) removeItemsByIds(paid.itemIds);
      }

      const prog = getCheckoutProgress(checkouts, paidIndex);
      setProgress(prog);

      if (prog.isComplete || checkouts.length === 0) {
        clearPendingCheckouts();
        clearCart();
        setFulfilling(false);
        setHandled(true);
        return;
      }

      // More vendors remain — stay on this page for explicit Continue
      setFulfilling(false);
      setHandled(true);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('session_id')]);

  const handleContinue = () => {
    if (!progress?.nextCheckout) return;
    const { checkouts } = loadPendingCheckouts();
    const nextIndex = (progress.paidCount); // next unpaid index
    if (nextIndex < checkouts.length) {
      savePendingCheckouts(checkouts, nextIndex);
      window.location.href = checkouts[nextIndex].checkoutUrl;
    }
  };

  const handleFinishLater = () => {
    clearPendingCheckouts();
    navigate('/my-purchases');
  };

  const paidName = progress?.paidCheckout?.vendorName;
  const next = progress?.nextCheckout;
  const multi = progress && progress.total > 1;

  return (
    <div className="max-w-md mx-auto py-12 px-4 text-center">
      <div className="rounded-2xl bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-8">
        <h1 className="text-2xl font-semibold text-green-800 dark:text-green-200 mb-2">
          {fulfilling && !handled
            ? 'Finalizing order…'
            : multi && !progress?.isComplete
              ? `Paid ${progress.paidCount} of ${progress.total}`
              : 'Payment complete'}
        </h1>

        {paidName && (
          <p className="text-lg text-green-700 dark:text-green-300 mb-2">
            Payment received for {paidName}.
          </p>
        )}

        {!multi || progress?.isComplete ? (
          <p className="text-lg text-green-700 dark:text-green-300 mb-2">
            {multi ? 'All vendors paid. Your orders are ready for pickup.' : 'Your order is ready for pickup.'}
          </p>
        ) : (
          <div className="text-left mb-4 space-y-2">
            <p className="text-sm text-green-800/90 dark:text-green-200/90">
              Still to pay:
            </p>
            <ul className="text-sm text-slate-800 dark:text-slate-200 space-y-1">
              {progress.remaining.map((c, i) => (
                <li key={`${c.vendorId}-${i}`}>
                  {c.vendorName}
                  {c.booth ? ` · Booth ${c.booth}` : ''}
                  {c.subtotal ? ` · $${Number(c.subtotal).toFixed(2)}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        {fulfillError && (
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
            {fulfillError} — if My Purchases is empty, wait a moment and refresh.
          </p>
        )}

        {multi && !progress?.isComplete && next ? (
          <div className="flex flex-col gap-3 mt-4">
            <button
              type="button"
              onClick={handleContinue}
              className="w-full px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
            >
              Continue to pay {next.vendorName} ({progress.paidCount + 1} of {progress.total})
            </button>
            <button
              type="button"
              onClick={handleFinishLater}
              className="w-full px-6 py-3 rounded-xl border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 font-medium"
            >
              Finish later — view paid pickups
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-green-800/80 dark:text-green-200/80 mb-6 mt-2">
              Open My Purchases and show each vendor&apos;s QR at their booth.
            </p>
            <button
              type="button"
              onClick={() => navigate('/my-purchases')}
              className="w-full px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
            >
              View pickup QR
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-3 w-full px-6 py-3 rounded-xl border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200 font-medium"
            >
              Back to marketplace
            </button>
          </>
        )}
      </div>
    </div>
  );
}
